<script>
export default {
    page() {
        return { title: 'Your Library' } // title only — noindex page
    },
}
</script>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from '@state/composables'
import Layout from '@layouts/main-layout'
import MixTableBlock from '@components/mix-table-block.v2'
import LibrarySectionTabs from '@components/library/library-section-tabs'
import LibraryHeader from '@components/library/library-header'
import LibrarySearchResults from '@components/library/library-search-results'
import LikedMixesCard from '@components/library/liked-mixes-card'

const RECENT_ACTIVITY_LIMIT = 10

const store = useStore()
const searchQuery = ref('')
const likedMixes = computed(() => store.getters['favorites/likedMixes'])
const mixesLoaded = computed(() => store.getters['favorites/isLoaded']('mixes'))
const recentActivity = computed(() => likedMixes.value.slice(0, RECENT_ACTIVITY_LIMIT))

onMounted(() => {
    // background refresh of what this page renders (mixes only) — favorites/init is
    // unguarded and would refire all four limit-100000 fetches on every visit
    store.dispatch('favorites/fetchType', 'mixes').catch(error => {
        // leave `loaded` untouched — the spinner (not a false empty state) is the FR21 failure mode
        console.error('Failed to refresh liked mixes', error)
    })
})
</script>

<template>
    <Layout>
        <BaseContent>
            <BaseSection padded>
                <LibraryHeader
                    title="Your Library"
                    :search-query="searchQuery"
                    @update:search-query="searchQuery = $event"
                />
                <LibrarySearchResults
                    v-if="searchQuery.trim()"
                    :query="searchQuery"
                />
                <template v-else>
                    <LibrarySectionTabs />
                    <LikedMixesCard :count="mixesLoaded ? likedMixes.length : null" />
                    <template v-if="recentActivity.length">
                        <h2 class="recent-title">Recently Added</h2>
                        <MixTableBlock
                            :mixes="recentActivity"
                            genres
                        />
                    </template>
                    <BaseSpinner v-else-if="!mixesLoaded" />
                    <BaseEmptyList
                        v-else
                        :error="{
                            title: 'No Mixes Yet',
                            paragraph: 'Like a mix? Tap the ♥ to add it to this list.',
                        }"
                    />
                </template>
            </BaseSection>
        </BaseContent>
    </Layout>
</template>

<style lang="scss" scoped>
@use '@design' as *;

.recent-title {
    @include sectionHeading;

    margin-bottom: $s-s;
}
</style>
